import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Alert,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  Chip,
  alpha,
  useTheme,
  Divider,
  Tooltip,
  FormControl,
  Select,
  Switch,
  FormControlLabel
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import WarningIcon from '@mui/icons-material/Warning';
import { styled } from '@mui/material/styles';
import AppAlert from '../../common/AppAlert';
import EmptyState from '../../common/EmptyState';
import LoadingState from '../../common/LoadingState';
import { supabase } from '../../../services/supabaseClient';
import dayjs from 'dayjs';

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  minQuantity: number;
  location: string;
  purchaseDate: string;
  expiryDate?: string;
  cost: number;
  supplier?: string;
  notes: string;
  status?: string;
  isAsset?: boolean;
  usefulLifeYears?: number;
  salvageValue?: number;
}

interface FarmInventoryProps {
  farm: any;
}

const inventoryCategories = [
  'Feed',
  'Seeds',
  'Fertilizers',
  'Pesticides',
  'Equipment',
  'Tools',
  'Medicine',
  'Supplies',
  'Other',
];

const commonUnits = [
  'kg',
  'g',
  'l',
  'ml',
  'pieces',
  'bags',
  'boxes',
  'units',
];

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
}));

const CategoryChip = styled(Chip)<{ category: string }>(({ theme, category }) => {
  // Generate a consistent color based on category string
  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ];
  const index = category.length % colors.length;
  const color = colors[index];

  return {
    backgroundColor: alpha(color, 0.1),
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
  };
});

interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  subtitle?: string;
}

const SummaryCard = ({ title, value, icon, color, subtitle }: SummaryCardProps) => {
  const theme = useTheme();
  // Safe access to palette colors
  const paletteColor = theme.palette[color] as any;
  
  return (
    <Card elevation={0} sx={{ 
      height: '100%', 
      borderRadius: 3, 
      border: `1px solid ${alpha(paletteColor.main, 0.2)}`,
      bgcolor: alpha(paletteColor.main, 0.05),
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3, '&:last-child': { pb: 3 } }}>
        <Box sx={{ 
          mr: 2, 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: alpha(paletteColor.main, 0.15),
          color: paletteColor.main,
          display: 'flex'
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight="600">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {value}
          </Typography>
          {subtitle && (
             <Typography variant="caption" color="text.secondary">
               {subtitle}
             </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * FarmInventory component for managing farm inventory items
 * Provides functionality to view, add, edit, and delete inventory items
 */
const FarmInventory: React.FC<FarmInventoryProps> = ({ farm }) => {
  const theme = useTheme();
  if (!farm) return <LoadingState message="Loading farm data..." />;
  const farmId = farm.id;

  // --- State for inventory data, loading, error, and success feedback ---
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // --- UI State ---
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [newItem, setNewItem] = useState<Partial<InventoryItem>>({
    name: '',
    category: '',
    quantity: 0,
    unit: '',
    minQuantity: 0,
    location: '',
    purchaseDate: dayjs().format('YYYY-MM-DD'),
    expiryDate: '',
    cost: 0,
    supplier: '',
    notes: '',
    isAsset: false,
    usefulLifeYears: 0,
    salvageValue: 0,
  });

  const fetchInventory = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('inventory')
      .select('*')
      .eq('farm_id', farmId)
      .order('created_at', { ascending: false });
    if (error) {
      setError('Failed to fetch inventory. ' + error.message);
      setInventory([]);
    } else {
      const mapped = (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        category: item.category,
        quantity: item.quantity ?? 0,
        unit: item.unit ?? '',
        minQuantity: item.min_quantity ?? 0,
        location: item.location ?? '',
        purchaseDate: item.purchase_date ?? '',
        expiryDate: item.expiry_date ?? '',
        cost: item.cost ?? 0,
        status: item.status ?? '',
        supplier: item.supplier ?? '',
        notes: item.notes ?? '',
        isAsset: item.is_asset ?? false,
        usefulLifeYears: item.useful_life_years ?? 0,
        salvageValue: item.salvage_value ?? 0,
      }));
      setInventory(mapped);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line
  }, [farmId]);

  // --- Derived State for Stats ---
  const stats = useMemo(() => {
    const totalItems = inventory.length;
    const lowStockItems = inventory.filter(i => i.quantity <= i.minQuantity).length;
    
    // Calculate total value including depreciation for assets
    let totalValue = 0;
    let assetValue = 0;
    let consumablesValue = 0;

    inventory.forEach(item => {
      let itemValue = 0;
      if (item.isAsset && item.usefulLifeYears && item.purchaseDate) {
        // Asset Valuation with Depreciation
        const purchaseDate = dayjs(item.purchaseDate);
        const now = dayjs();
        const yearsHeld = now.diff(purchaseDate, 'year', true);
        const usefulLife = Number(item.usefulLifeYears);
        const salvage = Number(item.salvageValue || 0);
        const cost = Number(item.cost);
        
        if (usefulLife > 0) {
          const depreciationPerYear = (cost - salvage) / usefulLife;
          const totalDepreciation = depreciationPerYear * yearsHeld;
          const currentValue = Math.max(salvage, cost - totalDepreciation);
          itemValue = currentValue * item.quantity; // Assuming quantity represents number of assets
          assetValue += itemValue;
        } else {
          itemValue = cost * item.quantity;
          assetValue += itemValue;
        }
      } else {
        // Standard Inventory Valuation
        itemValue = item.cost * item.quantity;
        consumablesValue += itemValue;
      }
      totalValue += itemValue;
    });

    const totalCategories = new Set(inventory.map(i => i.category)).size;
    return { totalItems, lowStockItems, totalValue, totalCategories, assetValue, consumablesValue };
  }, [inventory]);

  // --- Derived State for Filtering ---
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.supplier?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedItemId(itemId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItemId(null);
  };

  const handleEditFromMenu = () => {
    const item = inventory.find(i => i.id === selectedItemId);
    if (item) handleEditItem(item);
    handleMenuClose();
  };

  const handleDeleteFromMenu = () => {
    if (selectedItemId) handleDeleteItem(selectedItemId);
    handleMenuClose();
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setNewItem({
      name: '',
      category: '',
      quantity: 0,
      unit: '',
      minQuantity: 0,
      location: '',
      purchaseDate: dayjs().format('YYYY-MM-DD'),
      expiryDate: '',
      cost: 0,
      supplier: '',
      notes: '',
      isAsset: false,
      usefulLifeYears: 0,
      salvageValue: 0,
    });
    setOpenDialog(true);
    setError(null);
  };

  const handleEditItem = (item: InventoryItem) => {
    setEditingItem(item);
    setNewItem(item);
    setOpenDialog(true);
    setError(null);
  };

  const handleDeleteItem = async (itemId: string) => {
    setLoading(true);
    setError(null);
    const { error } = await supabase.from('inventory').delete().eq('id', itemId);
    if (error) {
      setError('Failed to delete item. ' + error.message);
    } else {
      setSuccess('Item deleted successfully.');
      await fetchInventory();
    }
    setLoading(false);
  };

  const handleSaveItem = async () => {
    if (!newItem.name || !newItem.category || !newItem.unit || !newItem.quantity || !newItem.location || !newItem.purchaseDate) {
      setError('Please fill in all required fields.');
      return;
    }
    setError(null);
    setLoading(true);
    const payload = {
      name: newItem.name,
      category: newItem.category,
      quantity: newItem.quantity,
      unit: newItem.unit,
      min_quantity: newItem.minQuantity ?? 0,
      location: newItem.location,
      purchase_date: newItem.purchaseDate ? newItem.purchaseDate : null,
      expiry_date: newItem.expiryDate ? newItem.expiryDate : null,
      cost: newItem.cost ?? 0,
      status: newItem.status ?? '',
      supplier: newItem.supplier ?? '',
      notes: newItem.notes ?? '',
      farm_id: farmId,
      is_asset: newItem.isAsset ?? false,
      useful_life_years: newItem.usefulLifeYears ?? 0,
      salvage_value: newItem.salvageValue ?? 0,
    };
    if (editingItem) {
      // Update item
      const { error } = await supabase
        .from('inventory')
        .update(payload)
        .eq('id', editingItem.id);
      if (error) {
        setError('Failed to update item. ' + error.message);
      } else {
        setSuccess('Item updated successfully.');
        setOpenDialog(false);
        await fetchInventory();
      }
    } else {
      // Add item
      const { data, error } = await supabase
        .from('inventory')
        .insert([payload])
        .select();
      if (error) {
        setError('Failed to add item. ' + error.message);
      } else {
        setSuccess('Item added successfully.');
        setOpenDialog(false);
        await fetchInventory();
      }
    }
    setLoading(false);
  };

  const handleInputChange = (field: keyof InventoryItem, value: string | number) => {
    setNewItem({ ...newItem, [field]: value });
  };

  const getLowStockItems = () => {
    return inventory.filter(item => item.quantity <= item.minQuantity);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Success Alert */}
      {success && (
        <AppAlert severity="success" message={success} onClose={() => setSuccess(null)} />
      )}
      {/* Error Alert */}
      {error && (
        <AppAlert severity="error" message={error} onClose={() => setError(null)} />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Inventory Management</Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
            <SummaryCard 
              title="Total Inventory Value" 
              value={`KES ${stats.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} 
              icon={<AttachMoneyIcon />} 
              color="success" 
              subtitle="Assets + Consumables"
            />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
            <SummaryCard 
              title="Asset Value" 
              value={`KES ${stats.assetValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} 
              icon={<AccountBalanceIcon />} 
              color="secondary" 
              subtitle="Depreciated Book Value"
            />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
            <SummaryCard 
              title="Consumables Value" 
              value={`KES ${stats.consumablesValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} 
              icon={<InventoryIcon />} 
              color="info" 
              subtitle="Stock Value"
            />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
            <SummaryCard title="Total Items" value={stats.totalItems} icon={<ViewListIcon />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
             <SummaryCard title="Low Stock" value={stats.lowStockItems} icon={<WarningIcon />} color="error" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
             <SummaryCard title="Categories" value={stats.totalCategories} icon={<CategoryIcon />} color="info" />
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <TextField
                placeholder="Search inventory..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
                sx={{ 
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        width: { xs: '100%', sm: 300 } 
                    } 
                }}
            />
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: 2 }}
                >
                    <MenuItem value="all">All Categories</MenuItem>
                    {inventoryCategories.map(c => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newView) => { if(newView) setViewMode(newView); }}
                size="small"
                sx={{ bgcolor: 'background.paper' }}
            >
                <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
                <ToggleButton value="list"><ViewListIcon /></ToggleButton>
            </ToggleButtonGroup>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
        >
          Add Item
        </Button>
      </Box>

      {/* Loading State */}
      {loading && <LoadingState message="Loading inventory..." />}

      {/* Empty State */}
      {!loading && inventory.length === 0 && (
        <EmptyState
          icon={<AddCircleOutlineOutlinedIcon fontSize="inherit" color="primary" />}
          title="No inventory items yet"
          description="Get started by adding your first inventory item."
          action={
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddItem}>
              Add Item
            </Button>
          }
        />
      )}

      {/* Low Stock Alert */}
      {!loading && inventory.length > 0 && getLowStockItems().length > 0 && (
        <AppAlert
          severity="warning"
          message={`${getLowStockItems().length} items are running low on stock`}
          sx={{ mb: 3 }}
        />
      )}

      {/* Inventory Grid/List View */}
      {!loading && filteredInventory.length > 0 && (
        viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {filteredInventory.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <StyledCard sx={{ 
                  borderLeft: item.quantity <= item.minQuantity ? `4px solid ${theme.palette.error.main}` : 'none'
                }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <CategoryChip category={item.category} label={item.category} size="small" />
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuClick(e, item.id)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Typography variant="h6" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.name}
                      {item.quantity <= item.minQuantity && (
                        <Tooltip title="Low Stock">
                          <WarningIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <InventoryIcon fontSize="small" />
                        <Typography variant="body2">
                          {item.quantity} {item.unit}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <LocationOnIcon fontSize="small" />
                        <Typography variant="body2">
                          {item.location}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <AttachMoneyIcon fontSize="small" />
                        <Typography variant="body2">
                          KES {item.cost.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: 'background.default' }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Purchase Date</TableCell>
                  <TableCell>Cost (KES)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInventory.map((item) => (
                  <TableRow 
                    key={item.id}
                    sx={{
                      backgroundColor: item.quantity <= item.minQuantity ? alpha(theme.palette.error.main, 0.05) : 'inherit',
                      '&:hover': { backgroundColor: item.quantity <= item.minQuantity ? alpha(theme.palette.error.main, 0.1) : theme.palette.action.hover },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.quantity <= item.minQuantity && (
                          <Tooltip title="Low Stock">
                            <WarningIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                        <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><CategoryChip category={item.category} label={item.category} size="small" /></TableCell>
                    <TableCell>{`${item.quantity} ${item.unit}`}</TableCell>
                    <TableCell>{item.location}</TableCell>
                    <TableCell>{item.purchaseDate}</TableCell>
                    <TableCell>{item.cost.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuClick(e, item.id)} size="small">
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { minWidth: 150 } }}
      >
        <MenuItem onClick={handleEditFromMenu}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>
          {editingItem ? 'Edit Item' : 'Add New Item'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 'bold' }}>
            Basic Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Name"
                value={newItem.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={newItem.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                required
                variant="outlined"
              >
                {inventoryCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
             <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes / Description"
                value={newItem.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                multiline
                rows={2}
                placeholder="Optional details about the item..."
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" color="primary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 'bold' }}>
              Asset Classification
            </Typography>
            <FormControlLabel
              control={
                <Switch 
                  checked={newItem.isAsset || false} 
                  onChange={(e) => setNewItem({ ...newItem, isAsset: e.target.checked })} 
                />
              }
              label="Track as Asset (Depreciable)"
            />
          </Box>
          
          {newItem.isAsset && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Useful Life (Years)"
                  type="number"
                  value={newItem.usefulLifeYears}
                  onChange={(e) => handleInputChange('usefulLifeYears', parseFloat(e.target.value))}
                  helperText="Estimated lifespan for depreciation"
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Salvage Value (KES)"
                  type="number"
                  value={newItem.salvageValue}
                  onChange={(e) => handleInputChange('salvageValue', parseFloat(e.target.value))}
                  helperText="Estimated value at end of life"
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                    inputProps: { min: 0 } 
                  }}
                />
              </Grid>
            </Grid>
          )}

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle2" color="primary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 'bold' }}>
            Stock Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={newItem.quantity}
                onChange={(e) => handleInputChange('quantity', parseFloat(e.target.value))}
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
               <TextField
                fullWidth
                select
                label="Unit"
                value={newItem.unit}
                onChange={(e) => handleInputChange('unit', e.target.value)}
                required
              >
                {commonUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Minimum Quantity (Alert)"
                type="number"
                value={newItem.minQuantity}
                onChange={(e) => handleInputChange('minQuantity', parseFloat(e.target.value))}
                helperText="Low stock alert threshold"
              />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost per Unit (KES)"
                type="number"
                value={newItem.cost}
                onChange={(e) => handleInputChange('cost', parseFloat(e.target.value))}
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                  inputProps: { min: 0 } 
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location / Storage"
                value={newItem.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                required
                placeholder="e.g. Barn A, Shelf 2"
              />
            </Grid>
          </Grid>

           <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle2" color="primary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 'bold' }}>
            Supplier & Dates
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier"
                value={newItem.supplier}
                onChange={(e) => handleInputChange('supplier', e.target.value)}
                placeholder="Vendor Name"
              />
            </Grid>
             <Grid item xs={12} sm={6}>
                 {/* Spacer or Status field if needed */}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Purchase Date"
                type="date"
                value={newItem.purchaseDate}
                onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={newItem.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Optional"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit" size="large">
            Cancel
          </Button>
          <Button onClick={handleSaveItem} variant="contained" size="large" disableElevation>
            {editingItem ? 'Save Changes' : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarmInventory;
